import { useState } from "react";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Check } from "lucide-react";
import OrentisLogo from "./OrentisLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadModal({ open, onOpenChange, trigger }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setForm({ name: "", email: "", company: "", message: "" });
    setStatus("idle");
    setErrorMsg("");
  };

  const handleOpenChange = (v) => {
    if (!v) {
      // slight delay so user sees success state before unmount reset
      setTimeout(reset, 250);
    }
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      setErrorMsg("Please share your email and a short message.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");

    // Safety: 20s timeout so users never get stuck on "sending"
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name || "Anonymous",
          email: form.email,
          company: form.company || null,
          message: form.message,
          source: "hero-modal",
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail =
          data?.detail?.[0]?.msg ||
          data?.detail ||
          `Request failed (${res.status})`;
        throw new Error(typeof detail === "string" ? detail : "Please try again.");
      }
      setStatus("success");
    } catch (err) {
      clearTimeout(timeoutId);
      setStatus("error");
      const msg =
        err?.name === "AbortError"
          ? "Taking longer than expected. Please email dinkar.chopra@orentisconsulting.in directly."
          : err?.message || "Something didn't click. Please try again or email us directly.";
      setErrorMsg(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger}
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-[80] bg-[#0e0e0e]/85 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[90] w-[92vw] max-w-[560px] -translate-x-1/2 -translate-y-1/2 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          data-testid="lead-modal"
        >
          <div className="relative bg-[var(--bg-2)] border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)]">
            {/* Gold hairline top */}
            <div className="h-px bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent opacity-70" />

            <DialogPrimitive.Close
              className="absolute right-4 top-4 w-9 h-9 rounded-full border border-white/15 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300 flex items-center justify-center text-[var(--ink-dim)] z-10"
              aria-label="Close"
              data-testid="lead-modal-close"
            >
              <X size={16} />
            </DialogPrimitive.Close>

            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <OrentisLogo size="sm" showTag={false} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] pl-3 border-l border-white/10">
                  Private Line
                </span>
              </div>

              <AnimatePresence mode="wait" initial={false}>
                {status !== "success" ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35 }}
                  >
                    <DialogPrimitive.Title asChild>
                      <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-[-0.01em] mb-3">
                        Start a{" "}
                        <span className="italic text-[var(--gold)] font-light">
                          conversation
                        </span>
                        .
                      </h3>
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description asChild>
                      <p className="text-[13px] leading-[1.6] text-[var(--ink-dim)] mb-7 max-w-[44ch]">
                        Share your ambition in a line or two. Dinkar personally
                        replies within one working day. No forms, no funnels.
                      </p>
                    </DialogPrimitive.Description>

                    <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
                      <div className="grid grid-cols-2 gap-4">
                        <FieldGroup label="Your Name" testid="lead-name">
                          <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="lead-input"
                            placeholder="Optional"
                            data-testid="lead-input-name"
                            autoComplete="name"
                          />
                        </FieldGroup>
                        <FieldGroup label="Company" testid="lead-company">
                          <input
                            type="text"
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            className="lead-input"
                            placeholder="Optional"
                            data-testid="lead-input-company"
                            autoComplete="organization"
                          />
                        </FieldGroup>
                      </div>

                      <FieldGroup label="Email" required testid="lead-email">
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="lead-input"
                          placeholder="you@company.com"
                          required
                          data-testid="lead-input-email"
                          autoComplete="email"
                        />
                      </FieldGroup>

                      <FieldGroup label="Message" required testid="lead-message">
                        <textarea
                          rows={3}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="lead-input resize-none"
                          placeholder="A brief on what you'd like to build, scale or reposition."
                          required
                          data-testid="lead-input-message"
                          maxLength={2000}
                        />
                      </FieldGroup>

                      {errorMsg && (
                        <div
                          className="text-[12px] tracking-[0.05em] text-[#f2a5a5] border-l-2 border-[#f2a5a5]/50 pl-3 py-1"
                          data-testid="lead-error"
                        >
                          {errorMsg}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-mute)]">
                          Est. reply · 24h
                        </div>
                        <button
                          type="submit"
                          disabled={status === "loading"}
                          className="group inline-flex items-center gap-3 bg-[var(--gold)] hover:bg-[var(--gold-soft)] disabled:opacity-60 disabled:cursor-wait text-[var(--bg)] transition-colors duration-300 px-6 py-3.5 rounded-full text-[11px] tracking-[0.28em] uppercase"
                          data-testid="lead-submit"
                        >
                          {status === "loading" ? (
                            <>
                              <span className="w-3 h-3 border-2 border-[var(--bg)]/30 border-t-[var(--bg)] rounded-full animate-spin" />
                              Sending
                            </>
                          ) : (
                            <>
                              Send message
                              <ArrowUpRight
                                size={14}
                                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
                              />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
                    className="py-4"
                    data-testid="lead-success"
                  >
                    <motion.div
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
                      className="w-14 h-14 rounded-full border border-[var(--gold)] flex items-center justify-center text-[var(--gold)] mb-6"
                    >
                      <Check size={22} strokeWidth={1.5} />
                    </motion.div>
                    <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-[-0.01em] mb-3">
                      Your note has{" "}
                      <span className="italic text-[var(--gold)] font-light">
                        arrived
                      </span>
                      .
                    </h3>
                    <p className="text-[13px] leading-[1.6] text-[var(--ink-dim)] mb-8 max-w-[44ch]">
                      We’ll be in touch within one working day at{" "}
                      <span className="text-[var(--ink)]">{form.email}</span>.
                      In the meantime, feel free to reach out directly.
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-white/10 text-[11px] tracking-[0.28em] uppercase">
                      <a
                        href="mailto:dinkar.chopra@orentisconsulting.in"
                        className="text-[var(--ink-dim)] hover:text-[var(--gold)] transition-colors duration-300 pt-5"
                      >
                        → Email Dinkar
                      </a>
                      <button
                        onClick={() => handleOpenChange(false)}
                        className="text-[var(--gold)] hover:text-[var(--gold-soft)] transition-colors duration-300 pt-5"
                        data-testid="lead-success-close"
                      >
                        Close
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

function FieldGroup({ label, required, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-mute)] mb-2">
        {label}
        {required && <span className="text-[var(--gold)] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
